var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import React from 'react';
import * as d3 from 'd3';
import { scaleLinear } from '@visx/scale';
import { typedMemo } from '../../utils/components/typedMemo';
import { Line } from '../../charts/Lines/components/Line';
import { TrackArea } from '../../charts/TrackArea';
import { colorPalette as defaultPalette, defaultMargins } from '../../charts/utils/';
import { useCharts } from '../../charts/components/Charts';
import { Axis } from '../../charts/Axis';
import { Canvas } from '../../charts/components/Canvas';
import { IntervalGuides } from '../../charts/components/IntervalGuides';
import { palette } from '../../constants';
import { EmptyGraph } from '../../charts/Lines/components/EmptyGraph';
import { Legend } from '../../charts/components/Legend';
/** Prevent unnecessary recalculation on higher-level renders */
var MemoedLine = typedMemo(Line);
var MemoedTrackArea = typedMemo(TrackArea);
function createLineGenerator(xScale, yScale, accessors) {
    var xAccessorDate = function (d) { return new Date(accessors.x(d)); };
    var xScaledAccessor = function (d) { var _a; return (_a = xScale(xAccessorDate(d))) !== null && _a !== void 0 ? _a : 0; };
    var yScaledAccessor = function (d) { var _a; return (_a = yScale(accessors.y(d))) !== null && _a !== void 0 ? _a : 0; };
    return d3.line().x(xScaledAccessor).y(yScaledAccessor);
}
export function WeatherChart(_a) {
    var name = _a.name, series = _a.series, accessors = _a.accessors, height = _a.height, xDomain = _a.xDomain, meta = _a.meta, _b = _a.colors, colors = _b === void 0 ? defaultPalette : _b, marginProps = _a.margins, _c = _a.emptyGraphMessage, emptyGraphMessage = _c === void 0 ? '' : _c, _d = _a.numTicks, numTicks = _d === void 0 ? 6 : _d, _e = _a.hideTicks, hideTicks = _e === void 0 ? true : _e, tooltip = _a.tooltip, axesProps = _a.axesProps;
    var width = useCharts().width;
    var margins = defaultMargins(marginProps);
    var boundedHeight = height - margins.top - margins.bottom;
    var _f = React.useState(undefined), hovered = _f[0], setHovered = _f[1];
    var boundedWidth = width - margins.left - margins.right;
    var valid = React.useMemo(function () {
        return Object.values(series).filter(function (s) { return accessors.valid !== undefined && accessors.valid(s) === 'valid'; });
    }, [series, accessors.valid]);
    var _g = React.useState(new Set(valid.map(function (d) { return accessors.key(d); }))), activeSeries = _g[0], setActiveSeries = _g[1];
    var active = React.useMemo(function () { return valid.filter(function (s) { return activeSeries.has(accessors.key(s)); }); }, [valid, activeSeries]);
    var xScale = React.useMemo(function () { return d3.scaleTime().domain(xDomain).range([0, boundedWidth]); }, [xDomain, boundedWidth]);
    var leftData = React.useMemo(function () { return accessors.data(series.left).map(function (d) { return accessors.y(d); }); }, [accessors, series]);
    var leftScale = React.useMemo(function () {
        return scaleLinear({
            range: [boundedHeight, 0],
            domain: [Math.min.apply(Math, __spreadArray([0], leftData)), Math.max.apply(Math, leftData)]
        });
    }, [boundedHeight, leftData]);
    var rightScale = React.useMemo(function () {
        return scaleLinear({
            range: [boundedHeight, 0],
            domain: [0, 100]
        });
    }, [boundedHeight]);
    var leftLineGenerator = React.useMemo(function () {
        return createLineGenerator(xScale, leftScale, accessors);
    }, [xScale, leftScale, accessors]);
    var rightLineGenerator = React.useMemo(function () {
        return createLineGenerator(xScale, rightScale, accessors);
    }, [xScale, rightScale, accessors]);
    var colorScheme = React.useMemo(function () {
        return d3
            .scaleOrdinal()
            .domain(Object.values(series).map(function (d) { return accessors.key(d); }))
            .range(colors);
    }, [series, colors]);
    var axisConfigs = React.useMemo(function () {
        var shared = { numTicks: numTicks, hideTicks: hideTicks, margins: margins };
        var left = __assign(__assign(__assign(__assign({}, shared), { orientation: 'left', isCanvasChild: true, height: height,
            margins: margins }), axesProps.left), { scale: leftScale });
        var right = __assign(__assign(__assign(__assign({}, shared), { orientation: 'right', isCanvasChild: true, height: height }), axesProps.right), { scale: rightScale });
        var bottom = __assign(__assign(__assign({}, shared), { orientation: 'bottom', scale: xScale.nice(), isCanvasChild: true, height: 40 }), axesProps.bottom);
        return {
            left: left,
            bottom: bottom,
            right: right,
            top: top
        };
    }, [axesProps, margins]);
    var clearHovered = React.useCallback(function () {
        setHovered(undefined);
    }, []);
    var handleHover = React.useCallback(function (id) {
        if (activeSeries.has(id)) {
            setHovered(id);
        }
    }, [activeSeries]);
    // Prevent hover effect from persisting after user has
    // deactivated a series (i.e., clicked on a legend chip)
    var handleLegendClick = React.useCallback(function (active, id) {
        if (id !== undefined && active.has(id) === true) {
            setHovered(id);
        }
        else {
            setHovered(undefined);
        }
        setActiveSeries(active);
    }, []);
    // Series data of chip currently being hovered, but only
    // if series is active
    var hoveredSeries = React.useMemo(function () {
        if (hovered === undefined || series[hovered] === undefined)
            return;
        var s = series[hovered];
        var key = accessors.key(s);
        return activeSeries.has(key) ? s : undefined;
    }, [hovered, series, accessors, activeSeries]);
    return (React.createElement(React.Fragment, null,
        active.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement(Canvas, { margins: margins, height: height },
                React.createElement(IntervalGuides, { xScale: xScale }),
                active.map(function (s) {
                    return (React.createElement(MemoedLine, { key: accessors.key(s), data: accessors.data(s), lineGenerator: accessors.key(s) === 'left'
                            ? leftLineGenerator
                            : rightLineGenerator, stroke: hovered === undefined
                            ? colorScheme(accessors.key(s))
                            : palette.neutral4, opacity: hovered === undefined ? 1 : 0.4 }));
                }),
                hoveredSeries !== undefined ? (React.createElement(MemoedLine, { key: accessors.key(hoveredSeries), data: accessors.data(hoveredSeries), lineGenerator: accessors.key(hoveredSeries) === 'left'
                        ? leftLineGenerator
                        : rightLineGenerator, stroke: colorScheme(accessors.key(hoveredSeries)), opacity: 1 })) : null,
                React.createElement(MemoedTrackArea, { pinId: name, height: height, series: active, accessors: accessors, xScale: xScale, meta: meta, colorScheme: colorScheme, tooltip: tooltip }),
                React.createElement("line", { x1: 0, x2: boundedWidth, y1: boundedHeight + margins.top, y2: boundedHeight + margins.top, stroke: palette.neutral4, strokeWidth: 1, shapeRendering: "optimizespeed" }),
                React.createElement(Axis, __assign({}, axisConfigs.right)),
                React.createElement(Axis, __assign({}, axisConfigs.left)),
                React.createElement(Axis, __assign({}, axisConfigs.bottom))))) : (React.createElement(EmptyGraph, { series: series, height: height, margins: margins, accessors: accessors, colorScheme: colorScheme, message: emptyGraphMessage })),
        React.createElement(Legend, { activeSet: activeSeries, series: series, accessors: accessors, colorScheme: colorScheme, onToggle: handleLegendClick, onMouseEnter: handleHover, onMouseLeave: clearHovered, marginLeft: margins === null || margins === void 0 ? void 0 : margins.left, marginRight: margins === null || margins === void 0 ? void 0 : margins.right })));
}
//# sourceMappingURL=WeatherChart.js.map